import type { CandidateJudgement, ScoredCandidate, ScoreSignal } from "@shared/labs/sourcing";
import {
  AlertCircle,
  BadgeCheck,
  ChevronDown,
  ExternalLink,
  EyeOff,
  GraduationCap,
  MapPin,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { Chip } from "./chip";
import { formatTenure, semanticStrength } from "./lib";

function isConcept(s: ScoreSignal): boolean {
  return s.key.startsWith("concept");
}

// The judge's level-fit verdict → a legible chip, indexed by `levelFit + 2` (-2 → 0 … +2 → 4).
// On-target is the ideal (good); the extremes (far below / over-qualified) are flagged (danger);
// adjacent levels are neutral.
const LEVEL_FIT: { label: string; tone: "good" | "default" | "danger" }[] = [
  { label: "Muito abaixo do nível", tone: "danger" }, // -2
  { label: "Abaixo do nível", tone: "default" }, // -1
  { label: "No nível", tone: "good" }, //  0
  { label: "Acima do nível", tone: "default" }, // +1
  { label: "Sobrequalificado", tone: "danger" }, // +2
];
const LIKELIHOOD = ["improvável", "plausível", "provável"];

export type Verdict = { label: string; tone: "good" | "default" | "danger" };

/** The judge's five anchored sub-judgements as legible verdicts, most-decision-relevant
 *  first (level fit → must-haves → nice-to-haves → pedigree). Drives both the collapsed
 *  preview (first two) and the full breakdown in the expanded panel. Empty for legacy
 *  verdicts (judged before the decomposition) that carry no breakdown. */
export function judgeVerdicts(fit: CandidateJudgement): Verdict[] {
  const out: Verdict[] = [];
  if (fit.levelFit !== undefined) {
    const lf = LEVEL_FIT[fit.levelFit + 2];
    if (lf) out.push(lf);
  }
  if (fit.mustHave !== undefined) {
    out.push({
      label: `Requisitos: ${LIKELIHOOD[fit.mustHave] ?? "—"}`,
      tone: fit.mustHave >= 2 ? "good" : fit.mustHave === 0 ? "danger" : "default",
    });
  }
  if (fit.niceHave) {
    out.push({
      label: `Diferenciais: ${LIKELIHOOD[fit.niceHave] ?? "—"}`,
      tone: fit.niceHave >= 2 ? "good" : "default",
    });
  }
  // Screening criteria (the brief's non-facet requirements, judged one by one): a chip only
  // for the DECIDED verdicts — verified (met=2, good) or contradicted (met=0, danger).
  // "Cannot tell" (met=1) hard criteria already surface as gap chips ("lacunas"), so they
  // are skipped here to avoid double display.
  for (const c of fit.criteria ?? []) {
    if (c.met >= 2) out.push({ label: c.statement, tone: "good" });
    else if (c.met === 0) out.push({ label: `Não atende: ${c.statement}`, tone: "danger" });
  }
  if (fit.eduPedigree) out.push({ label: "Formação de ponta", tone: "good" });
  if (fit.workPedigree) out.push({ label: "Empresas de ponta", tone: "good" });
  return out;
}

/** One "why this matched" strength chip — the semantic "reads like the role" signal gets
 *  a bar-meter rendering; everything else is a plain chip (concept hits read as `good`)
 *  with its point contribution to the score shown as a subtle suffix. */
function StrengthChip({ signal }: { signal: ScoreSignal }) {
  if (signal.key === "semantic") {
    const { bars, label } = semanticStrength(signal.detail ? Number.parseFloat(signal.detail) : 0);
    return (
      <span
        title={`cosseno ${signal.detail ?? "n/d"}`}
        className="inline-flex items-center gap-1.5 rounded-full border border-input bg-muted/40 px-2.5 py-0.5 text-xs text-foreground"
      >
        <span className="text-muted-foreground">Lê como a vaga</span>
        <span aria-hidden className="font-mono tracking-tighter">
          {"▰".repeat(bars)}
          <span className="text-muted-foreground/50">{"▱".repeat(5 - bars)}</span>
        </span>
        <span className="text-emerald-600 dark:text-emerald-400">{label}</span>
      </span>
    );
  }
  return (
    <Chip tone={isConcept(signal) ? "good" : "default"}>
      {signal.label}
      {signal.detail ? <span className="opacity-60"> · {signal.detail}</span> : null}
      {signal.contribution >= 1 ? (
        <span className="ml-1 font-mono text-[10px] opacity-50">
          +{Math.round(signal.contribution)}
        </span>
      ) : null}
    </Chip>
  );
}

/** A labelled block inside the LinkedIn profile zone (Resumo / Trajetória / Formação /
 *  Skills). The label is the same mono-caps eyebrow used for strengths/gaps; `empty`
 *  renders a muted "not informed on the profile" line when the section has no data. */
function ProfileSection({
  label,
  empty,
  children,
}: {
  label: string;
  empty?: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </span>
      {children ?? <p className="text-[12.5px] italic text-muted-foreground/70">{empty}</p>}
    </div>
  );
}

const EXP_PREVIEW = 3;
const SKILLS_PREVIEW = 12;

/**
 * One candidate result, scannable-first. Collapsed, the row is a compact triage
 * block — name + location, the current role · company · tenure, and the judge's
 * two headline verdicts + a gap count. Expanding reveals the full "why" (the
 * judge's breakdown, reasoning, and strengths vs gaps) AND the candidate's
 * LinkedIn profile: summary, work trajectory, education, and skills. All of the
 * profile data is already on the `candidate` prop — surfacing it is a UI choice,
 * no extra search/judge spend. Candidates are never blanked out.
 *
 * The headline shows rank, not the judge's raw FIT score. FIT is still what *orders*
 * the list (rank = position in the FIT-descending list), but it was read as a
 * percentage of quality — misleading, since a "100" can still carry gap chips. Rank
 * is an honest ordinal: "this is the Nth-best match", with the per-candidate evidence
 * (verdicts, strengths, gaps) carrying the actual quality signal.
 */
export function CandidateCard({
  candidate,
  fit,
  rank,
  selectable = false,
  selected = false,
  selectionDisabled = false,
  onToggleSelect,
  actions,
  source,
  defaultExpanded = false,
}: {
  candidate: ScoredCandidate;
  /** The LLM judge's fit verdict — drives the reasoning + sub-judgement chips. */
  fit: CandidateJudgement;
  /** 1-based position in the FIT-ranked list — the card's headline number. */
  rank: number;
  /** When true, render a selection checkbox (for "create an LA set from these"). */
  selectable?: boolean;
  selected?: boolean;
  /** Cap reached and this card is not yet selected → block further selection. */
  selectionDisabled?: boolean;
  onToggleSelect?: () => void;
  /** Optional caller-provided controls rendered in the collapsed row (e.g. the
   *  project page's stage buttons). A generic slot so the card stays flow-agnostic. */
  actions?: ReactNode;
  /** Optional "where this candidate entered from" badge (project triage only). Rendered
   *  in the expanded panel; search flows leave it unset so the card stays flow-agnostic. */
  source?: ReactNode;
  /** Start expanded — for the project triage board's detail pane, where the card
   *  IS the detail view (the full "why" should be visible without a second click). */
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [showAllExp, setShowAllExp] = useState(false);
  const [showAllSkills, setShowAllSkills] = useState(false);

  const concepts = candidate.signals.filter(isConcept);
  const others = candidate.signals.filter((s) => !isConcept(s));
  // Strengths are deterministic signals — a FOUND keyword/employer/school is a reliable positive.
  // (The `gap:*` signals stay filtered out: keyword *absence* is not a reliable negative, which is
  // exactly why gaps now come from the judge instead — see below.)
  const strengths = [...concepts, ...others].filter((s) => !s.key.startsWith("gap"));
  // Gaps are JUDGE-AUTHORED ("gaps to validate"), read by intent — so a founder is never flagged for
  // "ownership". Empty on legacy verdicts judged before the judge emitted gaps (graceful: no chips).
  const gaps = fit.gaps ?? [];

  const verdicts = judgeVerdicts(fit);
  // A thin profile (judge evidence == 0) is flagged so the recruiter treats the LinkedIn
  // data below as low-confidence and verifies at the source before acting.
  const thinProfile = fit.evidence === 0;

  // Collapsed line 2 — what they do now: current role · company · tenure. The company
  // comes from the current (top) experience entry (there's no dedicated field).
  const currentRole = candidate.currentTitle || candidate.experience[0]?.title;
  const currentCompany = candidate.experience[0]?.company;
  const roleLine = [currentRole, currentCompany].filter(Boolean).join(" · ");
  const tenure = formatTenure(candidate.currentTenureMonths);

  const experience = candidate.experience;
  const visibleExp = showAllExp ? experience : experience.slice(0, EXP_PREVIEW);
  const skills = candidate.skills;
  const visibleSkills = showAllSkills ? skills : skills.slice(0, SKILLS_PREVIEW);

  return (
    <article className="overflow-hidden rounded-xl border border-card-border bg-card text-card-foreground transition-colors hover:border-ring">
      {/* Collapsed row — compact triage block. */}
      <div className="flex items-center gap-3.5 p-4">
        {selectable && (
          <Checkbox
            checked={selected}
            onCheckedChange={() => onToggleSelect?.()}
            disabled={selectionDisabled && !selected}
            aria-label="Selecionar candidato para o pacote"
            className="shrink-0"
          />
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5">
            <a
              href={candidate.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-w-0 items-center gap-1 text-[15px] font-semibold leading-tight hover:underline"
            >
              <span className="truncate">{candidate.name || "Perfil sem nome"}</span>
              <ExternalLink size={12} className="shrink-0 text-muted-foreground" />
            </a>
            {candidate.location && (
              <span className="inline-flex shrink-0 items-center gap-0.5 text-[11px] text-muted-foreground">
                <MapPin size={11} />
                {candidate.location}
              </span>
            )}
          </div>

          {(roleLine || tenure) && (
            <div className="mt-1 flex items-center gap-1.5 text-[13px] text-muted-foreground">
              {roleLine && <span className="truncate">{roleLine}</span>}
              {roleLine && tenure && <span className="text-muted-foreground/40">·</span>}
              {tenure && <span className="shrink-0 whitespace-nowrap">{tenure}</span>}
            </div>
          )}

          {(verdicts.length > 0 || gaps.length > 0 || thinProfile) && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {verdicts.slice(0, 2).map((v) => (
                <Chip key={v.label} tone={v.tone}>
                  {v.label}
                </Chip>
              ))}
              {thinProfile && (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
                  <EyeOff size={12} />
                  baixa evidência
                </span>
              )}
              {gaps.length > 0 && (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-destructive">
                  <AlertCircle size={13} />
                  {gaps.length} {gaps.length === 1 ? "lacuna" : "lacunas"}
                </span>
              )}
            </div>
          )}
        </div>

        {actions && <div className="flex shrink-0 items-center gap-1.5">{actions}</div>}

        <div className="shrink-0 text-right">
          <div className="font-mono text-lg font-semibold leading-none tabular-nums text-foreground">
            #{rank}
          </div>
          <div className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground">
            rank
          </div>
        </div>

        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
          aria-label={expanded ? "Ocultar análise" : "Ver análise completa"}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/5"
        >
          <ChevronDown size={18} className={cn("transition-transform", expanded && "rotate-180")} />
        </button>
      </div>

      {/* Expanded panel — the full "why" plus the LinkedIn profile, aligned under the name. */}
      {expanded && (
        <div className="flex flex-col gap-3.5 border-t border-border py-3.5 pl-12 pr-4">
          {/* Where this candidate entered the project from (triage only). */}
          {source && (
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em]">
                Origem
              </span>
              {source}
            </div>
          )}

          {/* Zone A — the judge's verdict. */}
          {verdicts.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {verdicts.map((v) => (
                <Chip key={v.label} tone={v.tone}>
                  {v.label}
                </Chip>
              ))}
            </div>
          )}

          {fit.reasoning && (
            <p className="text-[13.5px] leading-relaxed text-foreground/80">{fit.reasoning}</p>
          )}

          {(strengths.length > 0 || gaps.length > 0) && (
            <div className="grid gap-4 sm:grid-cols-2">
              {strengths.length > 0 && (
                <div className="flex flex-col gap-2">
                  <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-600 dark:text-emerald-400">
                    Pontos fortes
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {strengths.map((s) => (
                      <StrengthChip key={s.key} signal={s} />
                    ))}
                  </div>
                </div>
              )}
              {gaps.length > 0 && (
                <div className="flex flex-col gap-2">
                  <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-destructive">
                    Lacunas a validar
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {gaps.map((g) => (
                      <Chip key={g} tone="danger">
                        {g}
                      </Chip>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Zone B — the LinkedIn profile. */}
          <div className="mt-1 flex items-center gap-2">
            <BadgeCheck size={14} className="shrink-0 text-muted-foreground" />
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Perfil LinkedIn
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          {thinProfile && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-[12.5px] leading-relaxed text-amber-700 dark:text-amber-300">
              <EyeOff size={14} className="mt-0.5 shrink-0" />
              <span>
                Perfil pouco legível — o juízo tem baixa evidência. Poucos cargos e descrições no
                LinkedIn. Confirme diretamente no perfil antes de avançar.
              </span>
            </div>
          )}

          <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
            {/* Left column: summary + trajectory. */}
            <div className="flex flex-col gap-4">
              <ProfileSection label="Resumo" empty="Resumo não informado no perfil.">
                {candidate.about ? (
                  <p className="line-clamp-5 text-[13px] leading-relaxed text-foreground/80">
                    {candidate.about}
                  </p>
                ) : undefined}
              </ProfileSection>

              <ProfileSection
                label="Trajetória"
                empty="Histórico profissional não informado no perfil."
              >
                {experience.length > 0 ? (
                  <div className="flex flex-col gap-3">
                    {visibleExp.map((e) => {
                      const dur = formatTenure(e.durationMonths);
                      const sub = [e.company, dur].filter(Boolean).join(" · ");
                      return (
                        <div
                          key={`${e.company ?? ""}·${e.title ?? ""}·${e.durationMonths ?? ""}·${
                            e.description ?? ""
                          }`}
                          className="flex flex-col gap-0.5"
                        >
                          <div className="text-[13px] font-medium leading-snug text-foreground">
                            {e.title || "Cargo não informado"}
                          </div>
                          {sub && <div className="text-[12px] text-muted-foreground">{sub}</div>}
                          {e.description && (
                            <p className="mt-0.5 line-clamp-2 text-[12px] leading-relaxed text-foreground/70">
                              {e.description}
                            </p>
                          )}
                        </div>
                      );
                    })}
                    {experience.length > EXP_PREVIEW && (
                      <button
                        type="button"
                        onClick={() => setShowAllExp((v) => !v)}
                        className="w-fit text-[12px] font-medium text-muted-foreground hover:text-foreground hover:underline"
                      >
                        {showAllExp
                          ? "Ver menos"
                          : `Ver mais ${experience.length - EXP_PREVIEW} cargo${
                              experience.length - EXP_PREVIEW === 1 ? "" : "s"
                            }`}
                      </button>
                    )}
                  </div>
                ) : undefined}
              </ProfileSection>
            </div>

            {/* Right column: education + skills. */}
            <div className="flex flex-col gap-4">
              <ProfileSection label="Formação" empty="Formação não informada.">
                {candidate.education.length > 0 ? (
                  <div className="flex flex-col gap-2.5">
                    {candidate.education.map((ed) => (
                      <div
                        key={`${ed.school ?? ""}·${ed.degree ?? ""}`}
                        className="flex items-start gap-2"
                      >
                        <GraduationCap
                          size={14}
                          className="mt-0.5 shrink-0 text-muted-foreground"
                        />
                        <div className="flex flex-col gap-0.5">
                          {ed.school && (
                            <div className="text-[13px] font-medium leading-snug text-foreground">
                              {ed.school}
                            </div>
                          )}
                          {ed.degree && (
                            <div className="text-[12px] text-muted-foreground">{ed.degree}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : undefined}
              </ProfileSection>

              <ProfileSection label="Skills" empty="Skills não informadas.">
                {skills.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {visibleSkills.map((sk) => (
                      <Chip key={sk}>{sk}</Chip>
                    ))}
                    {skills.length > SKILLS_PREVIEW && (
                      <button
                        type="button"
                        onClick={() => setShowAllSkills((v) => !v)}
                        className="text-[12px] font-medium text-muted-foreground hover:text-foreground hover:underline"
                      >
                        {showAllSkills ? "Ver menos" : `+${skills.length - SKILLS_PREVIEW} mais`}
                      </button>
                    )}
                  </div>
                ) : undefined}
              </ProfileSection>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}
